import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, Coins, Eye, Monitor, UserMinus, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { adminAPI, profitSharingAPI, unitAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasAdminPermission } from '../utils/adminPermissions';
import { formatIntegerCurrency, formatIntegerNumber, formatNumber, getUserDisplayName, getUserPhone } from '../utils/operations';

function SummaryCard({ icon: Icon, label, value, accent = 'cyan' }) {
  const accents = {
    cyan: 'border-cyan-300/25 bg-cyan-500/10 text-cyan-100',
    amber: 'border-amber-300/25 bg-amber-500/10 text-amber-100',
    emerald: 'border-emerald-300/25 bg-emerald-500/10 text-emerald-100',
    purple: 'border-violet-300/25 bg-violet-500/10 text-violet-100'
  };

  return (
    <div className={`rounded-xl border p-5 ${accents[accent] || accents.cyan}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-bold uppercase text-slate-200">{label}</span>
        <Icon size={18} className="shrink-0" />
      </div>
      <div className="mt-4 break-words text-2xl font-black text-white sm:text-3xl">{value}</div>
    </div>
  );
}

export default function KUDDetail() {
  const { t } = useTranslation();
  const currentAdmin = useAuthStore((state) => state.user);
  const canManagePartnerBindings = hasAdminPermission(currentAdmin, 'manage_partner_bindings');
  const canViewPartnerNetwork = hasAdminPermission(currentAdmin, 'view_partner_network');
  const canViewFinance = hasAdminPermission(currentAdmin, 'view_finance');
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [usersRes, unitsRes] = await Promise.all([
        adminAPI.getUsersAllPages({ role: 'KUD' }),
        unitAPI.getUnits()
      ]);
      const devicesRes = canViewPartnerNetwork
        ? await profitSharingAPI.getUserDevices({ userId: id, role: 'KUD' })
        : null;

      const kud = (usersRes.data?.data || []).find((entry) => String(entry.id) === String(id));
      if (!kud) {
        throw new Error(t('kudDetail.errors.notFound', { defaultValue: 'Partner PA not found' }));
      }

      const managedUnits = (unitsRes.data?.data || []).filter((unit) => String(unit.rpOwnerId) === String(id));
      setData({
        kud,
        managedUnits,
        revenue: devicesRes?.data?.data || { devices: [], totalProfit: 0 }
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('kudDetail.errors.load', { defaultValue: 'Failed to load Partner PA detail' }));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveKudRole = async () => {
    if (!canManagePartnerBindings) return;
    if (!data?.kud) return;
    const name = getUserDisplayName(data.kud);
    if (!window.confirm(t('kudDetail.removeRole.confirm', {
      defaultValue: 'Remove Partner PA role from {{name}}? Assigned units will be unbound, but account history stays.',
      name
    }))) {
      return;
    }

    setRemoving(true);
    try {
      const res = await adminAPI.removeUserRole(data.kud.id, 'KUD');
      const unboundUnits = res.data?.data?.unboundUnits ?? 0;
      alert(t('kudDetail.removeRole.success', {
        defaultValue: 'Partner PA role removed. {{count}} units unbound.',
        count: formatNumber(unboundUnits)
      }));
      navigate('/kuds');
    } catch (err) {
      alert(err.response?.data?.message || err.message || t('kudDetail.removeRole.failed', { defaultValue: 'Failed to remove Partner PA role' }));
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-slate-300 sm:p-6 lg:p-8">{t('kudDetail.loading', { defaultValue: 'Loading Partner PA detail...' })}</div>;
  }

  if (error || !data) {
    return (
      <div className="admin-text-primary space-y-4 p-4 sm:p-6 lg:p-8">
        <button onClick={() => navigate('/kuds')} className="inline-flex items-center gap-2 rounded-lg px-1 py-2 text-sm font-bold text-cyan-700">
          <ArrowLeft size={16} /> {t('kudDetail.back', { defaultValue: 'Back to Partner PA list' })}
        </button>
        <div className="rounded-xl border border-rose-300 bg-rose-50 p-6 text-rose-700">{error || t('kudDetail.errors.notFound', { defaultValue: 'Partner PA not found' })}</div>
      </div>
    );
  }

  const devices = data.revenue.devices || [];
  const totalRevenue = devices.reduce((sum, entry) => sum + Number(entry.totalRevenue || 0), 0);
  const totalVolume = devices.reduce((sum, entry) => sum + Number(entry.totalVolume || 0), 0);

  return (
    <div className="admin-text-primary space-y-6 p-4 sm:p-6 lg:p-8">
      <header className="admin-card rounded-xl p-5 sm:p-6">
        <button onClick={() => navigate('/kuds')} className="mb-5 inline-flex items-center gap-2 rounded-lg px-1 py-2 text-sm font-bold text-cyan-700 transition hover:text-cyan-800">
          <ArrowLeft size={16} /> {t('kudDetail.back', { defaultValue: 'Back to Partner PA list' })}
        </button>

        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h2 className="flex items-center gap-3 text-2xl font-black sm:text-3xl">
              <Briefcase className="shrink-0 text-cyan-300" size={30} />
              <span className="break-words">{getUserDisplayName(data.kud)}</span>
            </h2>
            <p className="mt-2 break-words text-sm leading-6 text-[var(--admin-text-muted)]">
              {getUserPhone(data.kud)} | Partner PA ID {data.kud.id}
            </p>
          </div>

          {canManagePartnerBindings && <button
            type="button"
            onClick={handleRemoveKudRole}
            disabled={removing}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-black text-rose-700 transition hover:bg-rose-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <UserMinus size={16} />
            {removing
              ? t('kudDetail.removeRole.processing', { defaultValue: 'Processing...' })
              : t('kudDetail.removeRole.action', { defaultValue: 'Remove Partner PA Role' })}
          </button>}
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard icon={Monitor} label={t('kudDetail.metrics.units', { defaultValue: 'Managed Units' })} value={formatNumber(data.managedUnits.length)} />
        {canViewPartnerNetwork && <SummaryCard icon={Coins} label={t('kudDetail.metrics.profit', { defaultValue: 'Partner PA Income Distribution' })} value={formatIntegerCurrency(data.revenue.totalProfit || 0)} accent="amber" />}
        {canViewPartnerNetwork && <SummaryCard icon={Briefcase} label={t('kudDetail.metrics.revenue', { defaultValue: 'Water Revenue' })} value={formatIntegerCurrency(totalRevenue)} accent="emerald" />}
        {canViewPartnerNetwork && <SummaryCard icon={Users} label={t('kudDetail.metrics.volume', { defaultValue: 'Total Volume' })} value={`${formatIntegerNumber(totalVolume)} L`} accent="purple" />}
      </section>

      {canViewPartnerNetwork && <section className="admin-card hidden overflow-x-auto rounded-xl lg:block">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="bg-[var(--admin-surface-soft)] text-xs font-black uppercase text-[var(--admin-text-muted)]">
            <tr>
              <th className="p-5">{t('kudDetail.table.unit', { defaultValue: 'Unit' })}</th>
              <th className="p-5">{t('kudDetail.table.revenue', { defaultValue: 'Revenue' })}</th>
              <th className="p-5">{t('kudDetail.table.volume', { defaultValue: 'Volume' })}</th>
              <th className="p-5">{t('kudDetail.table.profit', { defaultValue: 'Partner PA Income Distribution' })}</th>
            </tr>
          </thead>
          <tbody className="text-[var(--admin-text-secondary)]">
            {devices.map((entry) => (
              <tr key={`${entry.id}-${entry.unitId}`} className="border-t border-[var(--admin-border-subtle)] transition-colors hover:bg-[var(--admin-surface-soft)]">
                <td className="min-w-[260px] p-5">
                  <div className="font-bold text-[var(--admin-text-primary)]">{entry.unit?.deviceId || entry.deviceId || entry.unitId}</div>
                  <div className="mt-1 text-xs text-[var(--admin-text-muted)]">{entry.unit?.location || entry.unit?.deviceName || '-'}</div>
                </td>
                <td className="p-5 text-emerald-700">{formatIntegerCurrency(entry.totalRevenue || 0)}</td>
                <td className="p-5 text-[var(--admin-text-primary)]">{formatIntegerNumber(entry.totalVolume || 0)} L</td>
                <td className="p-5 font-bold text-amber-700">{formatIntegerCurrency(entry.rpProfit ?? entry.totalProfit ?? 0)}</td>
              </tr>
            ))}
            {devices.length === 0 && (
              <tr>
                <td colSpan="4" className="p-12 text-center text-[var(--admin-text-muted)]">
                  {t('kudDetail.empty', { defaultValue: 'No monthly Partner PA revenue data yet.' })}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>}

      {canViewPartnerNetwork && <section className="grid gap-3 lg:hidden">
        {devices.map((entry) => (
          <article key={`${entry.id}-${entry.unitId}`} className="admin-card rounded-xl p-4">
            <div className="font-black text-[var(--admin-text-primary)]">{entry.unit?.deviceId || entry.deviceId || entry.unitId}</div>
            <div className="mt-1 break-words text-sm text-[var(--admin-text-muted)]">{entry.unit?.location || entry.unit?.deviceName || '-'}</div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div className="admin-muted-panel rounded-lg p-3">
                <p className="text-xs font-bold uppercase text-[var(--admin-text-muted)]">{t('kudDetail.table.revenue', { defaultValue: 'Revenue' })}</p>
                <p className="mt-1 break-all text-base font-black text-emerald-700">{formatIntegerCurrency(entry.totalRevenue || 0)}</p>
              </div>
              <div className="admin-muted-panel rounded-lg p-3">
                <p className="text-xs font-bold uppercase text-[var(--admin-text-muted)]">{t('kudDetail.table.volume', { defaultValue: 'Volume' })}</p>
                <p className="mt-1 break-all text-base font-black text-[var(--admin-text-primary)]">{formatIntegerNumber(entry.totalVolume || 0)} L</p>
              </div>
              <div className="admin-muted-panel rounded-lg p-3">
                <p className="text-xs font-bold uppercase text-[var(--admin-text-muted)]">{t('kudDetail.table.profit', { defaultValue: 'Partner PA Income Distribution' })}</p>
                <p className="mt-1 break-all text-base font-black text-amber-700">{formatIntegerCurrency(entry.rpProfit ?? entry.totalProfit ?? 0)}</p>
              </div>
            </div>
          </article>
        ))}

        {devices.length === 0 && (
          <div className="admin-card rounded-xl p-8 text-center">
            {t('kudDetail.empty', { defaultValue: 'No monthly Partner PA revenue data yet.' })}
          </div>
        )}
      </section>}

      {canViewFinance && <Link
        to="/profit-sharing-finance"
        className="admin-primary-button inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-black transition focus:outline-none focus:ring-2 focus:ring-cyan-200"
      >
        <Eye size={16} />
        {t('kudDetail.financeLink', { defaultValue: 'View Income Distribution Finance' })}
      </Link>}
    </div>
  );
}
